import { useRouter } from 'next/router';
import { Form, Formik } from 'formik';
import { Button, Typography, Loader, Stack, Chip } from '@krakentech/coral';
import { FormikTextField } from '@krakentech/coral-formik';
import { IconSearch, IconDismiss } from '@krakentech/icons';
import FaqList from '../FaqList';

import { DataSourceEntry } from '@/types/storyblok';

import ScreenReaderOnly from '@/components/ScreenReaderOnly';

import { FaqList as FaqListType } from '@/hooks/help-and-faqs/faqs/types';
import { PageContainer } from '@/components/PageContainer';

type FaqSearchProps = {
    searchResults?: FaqListType;
    categories?: DataSourceEntry[];
    currentSearchTerm?: string;
    currentCategory?: DataSourceEntry;
    currentFaqSlug?: string;
    isLoading: boolean;
};
const FaqSearch = ({
    searchResults,
    categories,
    currentSearchTerm,
    currentCategory,
    currentFaqSlug,
    isLoading,
}: FaqSearchProps) => {
    const router = useRouter();

    const selectSearchTerm = (newSearchTerm: string | undefined) => {
        interface queryParamsType {
            search: string | undefined;
            category?: string;
            slug?: string;
        }
        const queryParams: queryParamsType = {
            search: newSearchTerm ? encodeURI(newSearchTerm) : undefined,
        };

        /**
         * params must be checked and added individually as undefined appends an unecessary empty query param
         * e.g. ?category=&search=
         */
        if (currentCategory) {
            queryParams.category = currentCategory.name;
        }
        if (currentFaqSlug) {
            queryParams.slug = currentFaqSlug;
        }

        router.replace(
            {
                // Type 'queryParamsType' is not assignable to type 'string | ParsedUrlQueryInput | null | undefined'.
                // @ts-ignore
                query: queryParams,
            },
            undefined,
            { shallow: true, scroll: false }
        );
    };

    return (
        <PageContainer>
            <Stack direction="vertical" justifyContent="center">
                {categories && categories.length > 0 ? (
                    <>
                        <Typography variant="h3" component="h2">
                            Select an FAQ Category
                        </Typography>

                        {categories.map((category) => (
                            <Chip
                                href={`/help-and-faqs/categories/${encodeURIComponent(
                                    category.name
                                )}`}
                                color={
                                    currentCategory &&
                                    currentCategory.name === category.name
                                        ? 'primary'
                                        : 'secondary'
                                }
                                key={category.name}
                            />
                        ))}
                    </>
                ) : null}

                <Typography variant="h3" component="h2">
                    Enter a search term
                </Typography>

                <Formik
                    initialValues={{
                        searchTerm: '',
                    }}
                    validate={(values) => {
                        const errors: { searchTerm?: string } = {};
                        if (!values.searchTerm) {
                            errors.searchTerm =
                                'Please enter a valid search term';
                        } else if (!/^[a-zA-Z]+/.test(values.searchTerm)) {
                            errors.searchTerm = 'Invalid search term';
                        }
                        return errors;
                    }}
                    onSubmit={(values) => {
                        selectSearchTerm(values.searchTerm);
                    }}
                >
                    {({ values, handleSubmit, resetForm }) => {
                        return (
                            <Form onSubmit={handleSubmit}>
                                <Stack gap="md">
                                    <FormikTextField
                                        type="text"
                                        name="searchTerm"
                                        label="What can we help you with?"
                                        placeholder={currentSearchTerm}
                                    />
                                    <Button
                                        type="submit"
                                        color="secondary"
                                        startIcon={<IconSearch />}
                                    >
                                        Search
                                    </Button>
                                    {currentSearchTerm && values.searchTerm ? (
                                        <Button
                                            onClick={() => {
                                                resetForm();
                                                selectSearchTerm(undefined);
                                            }}
                                            color="secondary"
                                            variant="outlined"
                                            startIcon={<IconDismiss />}
                                        >
                                            Clear
                                            <ScreenReaderOnly>
                                                search term
                                            </ScreenReaderOnly>
                                        </Button>
                                    ) : null}
                                </Stack>
                            </Form>
                        );
                    }}
                </Formik>
                {currentSearchTerm ? (
                    <>
                        <Typography variant="h4" component="h3">
                            {currentCategory ? (
                                <>
                                    FAQs in the &lsquo;
                                    {currentCategory.value}
                                    &rsquo; category containing the term &lsquo;
                                    {currentSearchTerm}
                                    &rsquo;
                                </>
                            ) : (
                                <>
                                    FAQs containing the term &lsquo;
                                    {currentSearchTerm}
                                    &rsquo;
                                </>
                            )}
                        </Typography>
                        {isLoading ? (
                            <Loader type="linear" />
                        ) : (
                            <FaqList faqs={searchResults} />
                        )}
                    </>
                ) : null}
            </Stack>
        </PageContainer>
    );
};

export default FaqSearch;
